/**
 * Currency Service
 * Handles currency conversion using Frankfurter API
 * https://www.frankfurter.app/
 */

import type { CurrencyRate } from '../types/database';
import { logger } from './logger';

const FRANKFURTER_API = 'https://api.frankfurter.app';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

// In-memory cache for rates
let cachedRates: CurrencyRate | null = null;
let cacheTimestamp: number = 0;

/**
 * Supported currencies for marketplace pricing
 */
export const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
] as const;

export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number]['code'];

/**
 * Fetch latest exchange rates from Frankfurter API
 * Base currency is always USD
 */
export async function fetchExchangeRates(): Promise<CurrencyRate> {
  const now = Date.now();

  // Return cached rates if still valid
  if (cachedRates && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedRates;
  }

  try {
    const currencies = SUPPORTED_CURRENCIES
      .map(c => c.code)
      .filter(c => c !== 'USD')
      .join(',');

    const response = await fetch(`${FRANKFURTER_API}/latest?from=USD&to=${currencies}`);

    if (!response.ok) {
      throw new Error(`Frankfurter API error: ${response.status}`);
    }

    const data = await response.json();

    cachedRates = {
      base: data.base,
      date: data.date,
      rates: {
        USD: 1, // Add USD rate (1:1)
        ...data.rates,
      },
    };
    cacheTimestamp = now;

    return cachedRates;
  } catch (error) {
    logger.error('Failed to fetch exchange rates:', error);

    // Return cached rates if available, even if expired
    if (cachedRates) {
      logger.warn('Using expired cached rates');
      return cachedRates;
    }

    // Return fallback rates if no cache available
    return getFallbackRates();
  }
}

/**
 * Convert amount from one currency to another
 */
export async function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string
): Promise<number> {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  const rates = await fetchExchangeRates();

  // Convert to USD first (if not already USD)
  let amountInUSD = amount;
  if (fromCurrency !== 'USD') {
    const fromRate = rates.rates[fromCurrency];
    if (!fromRate) {
      throw new Error(`Unsupported currency: ${fromCurrency}`);
    }
    amountInUSD = amount / fromRate;
  }

  // Convert from USD to target currency
  if (toCurrency === 'USD') {
    return amountInUSD;
  }

  const toRate = rates.rates[toCurrency];
  if (!toRate) {
    throw new Error(`Unsupported currency: ${toCurrency}`);
  }

  return amountInUSD * toRate;
}

/**
 * Get exchange rate between two currencies
 */
export async function getExchangeRate(
  fromCurrency: string,
  toCurrency: string
): Promise<number> {
  if (fromCurrency === toCurrency) {
    return 1;
  }

  const rates = await fetchExchangeRates();

  // Get rate from USD to target
  if (fromCurrency === 'USD') {
    return rates.rates[toCurrency] || 1;
  }

  // Get rate from source to USD, then to target
  const fromRate = rates.rates[fromCurrency] || 1;
  const toRate = rates.rates[toCurrency] || 1;

  return toRate / fromRate;
}

/**
 * Format currency value with proper symbol and decimals
 */
export function formatCurrency(
  amount: number,
  currencyCode: string,
  options?: {
    showSymbol?: boolean;
    decimals?: number;
    useGrouping?: boolean;
  }
): string {
  const { showSymbol = true, decimals = 2, useGrouping = true } = options || {};

  const currency = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
  const symbol = currency?.symbol || currencyCode;

  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  });

  return showSymbol ? `${symbol}${formatted}` : formatted;
}

/**
 * Get currency symbol for a currency code
 */
export function getCurrencySymbol(currencyCode: string): string {
  const currency = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
  return currency?.symbol || currencyCode;
}

/**
 * Fallback rates when API is unavailable
 * These are approximate rates and should be updated periodically
 */
function getFallbackRates(): CurrencyRate {
  return {
    base: 'USD',
    date: new Date().toISOString().split('T')[0],
    rates: {
      USD: 1,
      EUR: 0.92,
      GBP: 0.79,
      TRY: 34.50,
      CAD: 1.36,
      AUD: 1.53,
      JPY: 149.50,
      MXN: 17.15,
      INR: 83.30,
      BRL: 4.97,
      PLN: 3.98,
      SEK: 10.45,
      SGD: 1.34,
      AED: 3.67,
      SAR: 3.75,
      ZAR: 18.70,
    },
  };
}

/**
 * Clear the exchange rate cache
 * Useful for forcing a refresh
 */
export function clearRateCache(): void {
  cachedRates = null;
  cacheTimestamp = 0;
}

/**
 * Get cached rates without fetching
 * Returns null if no cache available
 */
export function getCachedRates(): CurrencyRate | null {
  return cachedRates;
}

/**
 * Check if cache is still valid
 */
export function isCacheValid(): boolean {
  return cachedRates !== null && (Date.now() - cacheTimestamp) < CACHE_DURATION;
}
